// Sheep processing-batch READS and row shapes. This module owns no writes.
//
// It used to carry the client-side send that the login-gated webform ran:
// create or promote the batch, then write the roster, the animal, the transfer
// and the weigh-in linkage as separate statements. Both sheep entry points now
// call attach_sheep_to_processing_batch, which does all of it in one
// transaction under its own row locks, so that second path is gone and nothing
// here may grow a write back. Detach is likewise owned exclusively by migration
// 170's transactional SECDEF wrapper.

import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';

// Row shapes below describe ONLY the columns these helpers read or write. Every
// other column of the real table stays out of the type: `select('*')` hands over
// whole rows and the client has no generated database types to check them
// against, so an index signature carries the rest as `unknown`.

// One entry of the batch's sheep_detail jsonb array.
export type SheepDetailRow = {
  sheep_id?: string | null;
  tag?: string | null;
  live_weight?: number | string | null;
  hanging_weight?: number | string | null;
};

// A prior tag carried on sheep.old_tags. `source` distinguishes WCF's own
// renumbering from an imported selling-farm tag, which the matcher excludes.
export type SheepOldTag = {tag?: string | null; source?: string | null};

export type SheepRow = {
  id: string;
  tag?: string | null;
  flock?: string | null;
  old_tags?: SheepOldTag[] | null;
  [column: string]: unknown;
};

export type SheepProcessingBatchRow = {
  id: string;
  name?: string | null;
  sheep_detail?: SheepDetailRow[] | null;
  [column: string]: unknown;
};

// A batch row as the send-to-processor picker reads it: the shape above plus
// the one extra column that dropdown renders and orders by. Declared here
// rather than in the modal because the loader below is what selects it.
export type PlannedSheepProcessingBatchRow = SheepProcessingBatchRow & {planned_process_date?: string | null};

// The planned-batch picker's read, moved out of SheepSendToProcessorModal.
//
// DELIBERATELY NOT `async`. The modal issues this WITHOUT awaiting it — a bare
// `.then()` continuation inside a `useEffect`, guarded by that effect's
// `cancelled` cleanup flag — and an `async` wrapper would insert an extra
// microtask hop between the request and the continuation. Returning the
// builder itself keeps the call site's timing byte-for-byte what it was, which
// is the whole contract of a transport extraction.
//
// The RAW `{data, error}` pair for the same reason: the modal renders its own
// inline `'Could not load planned batches: ' + error.message` notice and
// clears its loading flag; a helper that threw would route that into a catch
// the effect does not have.
//
// The full filter/order contract is here rather than at the call site because
// it IS the request: every UNFINISHED batch, only ones not yet processed,
// oldest planned date first, and nulls last so an undated batch never displaces
// a scheduled one at the top of the picker.
//
// 'active' joins 'planned' with migration 206. A batch that has already had one
// send is still a legitimate destination for a second — the RPC accepts it, and
// "Send more sheep" on an in-process batch record leads straight here — so
// filtering it out would silently drop those sheep into a NEW batch instead.
// `actual_process_date IS NULL` still means "not yet processed", which is why a
// send deliberately does not stamp that column -- see the promotion note in
// migration 206 -- so an in-process batch still reads as not yet processed.
export function loadPlannedSheepProcessingBatches(sb: SupabaseClient): PromiseLike<{
  data: PlannedSheepProcessingBatchRow[] | null;
  error: SupabaseErrorLike | null;
}> {
  return sb
    .from('sheep_processing_batches')
    .select('*')
    .in('status', ['planned', 'active'])
    .is('actual_process_date', null)
    .order('planned_process_date', {ascending: true, nullsFirst: false});
}

// Ask the server for the next free S-YY-NN batch name. Migration 206 owns the
// allocation because it is the only place that can see EVERY batch: the picker
// read above is deliberately planned-only, and deriving a name from it proposed
// numbers already taken by completed batches, which the UNIQUE constraint on
// name then rejected. Callers use this for the name they SHOW; the attach RPC
// re-allocates internally if that name is taken by the time it commits.
export async function nextSheepBatchName(sb: SupabaseClient): Promise<string> {
  const {data, error} = await sb.rpc('next_sheep_processing_batch_name', {});
  if (error) throw new Error('Could not allocate a batch name: ' + error.message);
  const name = typeof data === 'string' ? data.trim() : '';
  if (!name) throw new Error('Could not allocate a batch name: empty allocation');
  return name;
}
